import { supabase } from '@/lib/supabase';

export type GovernorLiveActionType =
  | 'member_add_attempt'
  | 'member_add_success'
  | 'member_add_fail'
  | 'coparent_child_selected'
  | 'media_upload_attempt'
  | 'media_storage_success'
  | 'media_storage_fail'
  | 'family_graph_photos_insert_success'
  | 'family_graph_photos_insert_fail'  | 'governor_calls_runtime_event'
;

export type GovernorLiveAction = {
  id: string;
  type: GovernorLiveActionType;
  detail: Record<string, any>;
  createdAt: string;
};

const KEY = 'kinscircle_governor_live_actions';

export function getGovernorLiveActions(): GovernorLiveAction[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
}

export function emitGovernorLiveAction(type: GovernorLiveActionType, detail: Record<string, any> = {}) {
  const action: GovernorLiveAction = {
    id: crypto?.randomUUID?.() || String(Date.now()),
    type,
    detail,
    createdAt: new Date().toISOString(),
  };
  const next = [action, ...getGovernorLiveActions()].slice(0, 100);
  localStorage.setItem(KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent('governor-live-action', { detail: action }));
  return action;
}

export function subscribeGovernorLiveActions(cb: (actions: GovernorLiveAction[]) => void) {
  const handler = () => cb(getGovernorLiveActions());
  window.addEventListener('governor-live-action', handler);
  window.addEventListener('storage', handler);
  cb(getGovernorLiveActions());
  return () => {
    window.removeEventListener('governor-live-action', handler);
    window.removeEventListener('storage', handler);
  };
}

export type CallsGovernorRuntimeStatus =
  | 'attempt'
  | 'success'
  | 'failure'
  | 'waiting'
  | 'timeout'
  | 'ended';

export interface CallsGovernorRuntimeEventDetail {
  readonly domain?: 'calls';
  readonly governor?: 'calls-governor';
  readonly runtime?: true;
  readonly stage: string;
  readonly status?: CallsGovernorRuntimeStatus;
  readonly source?: string;
  readonly callSessionId?: string | null;
  readonly callId?: string | null;
  readonly roomName?: string;
  readonly selfEmail?: string;
  readonly peerEmail?: string;
  readonly callerEmail?: string;
  readonly receiverEmail?: string;
  readonly mode?: string;
  readonly reason?: string;
  readonly error?: string;
  readonly timestamp?: string;
  readonly [key: string]: unknown;
}

const normalizeCallsStatus = (
  value: unknown,
): CallsGovernorRuntimeStatus => {
  const normalized =
    String(value ?? 'attempt')
      .trim()
      .toLowerCase();

  if (
    normalized === 'success' ||
    normalized === 'failure' ||
    normalized === 'waiting' ||
    normalized === 'timeout' ||
    normalized === 'ended'
  ) {
    return normalized;
  }

  return 'attempt';
};

const persistCallsRuntimeEvent = async (
  detail: CallsGovernorRuntimeEventDetail,
): Promise<void> => {
  const callSessionId =
    String(
      detail.callSessionId ??
      detail.callId ??
      '',
    ).trim();

  if (!callSessionId) {
    return;
  }

  const stage =
    String(
      detail.stage ??
      detail.step ??
      'CALL_RUNTIME_EVENT',
    ).trim();

  const persistedDetails: Record<string, unknown> = {
    ...detail,
    visibilityState:
      typeof document === 'undefined'
        ? 'unknown'
        : document.visibilityState,
    online:
      typeof navigator === 'undefined'
        ? null
        : navigator.onLine,
    runtime:
      typeof window === 'undefined'
        ? 'unknown'
        : (
            (window as any).Capacitor
              ? 'capacitor'
              : 'browser'
          ),
  };

  delete persistedDetails.callSessionId;
  delete persistedDetails.callId;
  delete persistedDetails.stage;
  delete persistedDetails.status;
  delete persistedDetails.source;
  delete persistedDetails.reason;
  delete persistedDetails.error;

  try {
    const {
      error,
    } = await (
      supabase as any
    ).rpc(
      'persist_calls_runtime_evidence',
      {
        p_call_session_id:
          callSessionId,
        p_stage:
          stage,
        p_status:
          normalizeCallsStatus(
            detail.status,
          ),
        p_source:
          detail.source ??
          'unknown',
        p_mode:
          detail.mode ??
          null,
        p_reason:
          detail.reason ??
          null,
        p_error:
          detail.error ??
          null,
        p_details:
          persistedDetails,
      },
    );

    if (error) {
      console.warn(
        '[Calls Governor] persistent evidence failed:',
        error,
      );
    }
  } catch (error) {
    console.warn(
      '[Calls Governor] persistent evidence crashed:',
      error,
    );
  }
};

export function emitCallsRuntimeEvent(
  detail: CallsGovernorRuntimeEventDetail,
): GovernorLiveAction {
  const normalizedDetail = {
    ...detail,
    stage:
      detail.stage ??
      String(
        detail.step ??
        'CALL_RUNTIME_EVENT',
      ),
    domain: 'calls' as const,
    governor: 'calls-governor' as const,
    runtime: true as const,
    timestamp:
      detail.timestamp ??
      new Date().toISOString(),
  };

  const action =
    emitGovernorLiveAction(
      'governor_calls_runtime_event',
      normalizedDetail,
    );

  void persistCallsRuntimeEvent(
    normalizedDetail,
  );

  return action;
}
